import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDefault } from 'vl-common/src/hooks/useDefault';
import { useCallback, useEffect, useState } from 'react';

function getListFromResult(data) {
  if (!data) return null;

  if (Array.isArray(data)) return data;

  const arrayValues = Object.values(data).filter((d) => Array.isArray(d));

  if (arrayValues.length !== 1) {
    return null;
  }

  return arrayValues[0];
}

// thunk is a function that takes no arguments and returns a promise that yields the data
// dependentValues is a list of all values used by the thunk
export function usePagedQuery(
  thunk,
  [queryName, ...dependentValues],
  pageNumber,
  setPageNumber,
  pageSize = 10,
  isTabbedPage = true
) {
  const [totalPages, setTotalPages] = useState(Infinity);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const checkAndPurgePlaceholder = (previousData, previousQuery) => {
    // no placeholder cache if no prev query
    if (!previousQuery) return undefined;

    const [prevQueryName, prevDeps] = previousQuery.queryKey;
    const [currentQueryName, currentDeps] = [queryName, dependentValues];

    // purge placeholder if query name is different
    if (prevQueryName !== currentQueryName) return undefined;

    const prevActiveTab = prevDeps[prevDeps.length - 1];
    const currentActiveTab = currentDeps[currentDeps.length - 1];

    // purge placeholder if active tab is different
    if (prevActiveTab !== currentActiveTab) return undefined;

    // keep placeholder if query name and active tab are the same (pagination/filtering)
    return previousData;
  };

  const result = useQuery<any>({
    queryKey: [queryName, dependentValues],
    queryFn: thunk,
    placeholderData: !isTabbedPage
      ? keepPreviousData
      : (previousData, previousQuery) => checkAndPurgePlaceholder(previousData, previousQuery),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const rows = getListFromResult(result.data?.payload || result.data) as [];
  const list = useDefault(rows, []);
  const { status, isFetching } = result;

  // When a different table is selected, the lastPage/totalPages needs to be reset.
  useEffect(() => {
    if (result.data) {
      setTotalPages(Infinity);
    }
  }, [result.data]);

  useEffect(() => {
    if (status === 'success') {
      setLoading(false);
      if (!rows?.length) {
        setTotalPages(pageNumber);
      }
      if (rows?.length > 0 && rows.length !== pageSize) {
        setTotalPages(pageNumber);
      }
    }
  }, [rows, status, pageSize]);

  return {
    page: list,
    prev: useCallback(() => setPageNumber((p) => Math.max(p - 1, 0)), [setPageNumber]),
    next: useCallback(() => setPageNumber((p) => Math.min(p + 1, totalPages)), [setPageNumber, totalPages]),
    totalPages,
    lastPage: pageNumber === totalPages,
    loading,
    setLoading,
    refetch: useCallback((pageNumber = 1) => {
      setTotalPages(Infinity);
      setPageNumber(pageNumber);
      queryClient.invalidateQueries({
        queryKey: [queryName]
      });
    }, []),
    busy: isFetching,
    error: result.error
  };
}

export default usePagedQuery;
